/* ==========================================================================
   COMPARE MACHINES TABLE
   - Sincroniza el scroll horizontal de las cards con el de la tabla
   - Filtros All / Commercial / Residential (oculta columnas)
   - "Show only differences" (oculta filas donde todas las columnas visibles
     tienen el mismo valor)
   - Barra de progreso + flechas
   ========================================================================== */

(function () {
  'use strict';

  function init(root) {
    if (root.dataset.cmtReady === 'true') return;
    root.dataset.cmtReady = 'true';

    var cardsView = root.querySelector('[data-cmt-sync="cards"]');
    var tableView = root.querySelector('[data-cmt-sync="table"]');
    var thumb     = root.querySelector('[data-cmt-thumb]');
    var diffInput = root.querySelector('[data-cmt-diff]');
    var noDiffMsg = root.querySelector('[data-cmt-nodiff]');
    var jumpBtn   = root.querySelector('[data-cmt-jump]');
    var anchor    = root.querySelector('[data-cmt-anchor]');

    var filterBtns = Array.prototype.slice.call(root.querySelectorAll('[data-cmt-filter]'));
    var navBtns    = Array.prototype.slice.call(root.querySelectorAll('[data-cmt-nav]'));
    var cards      = Array.prototype.slice.call(root.querySelectorAll('.cmt__card'));
    var rows       = Array.prototype.slice.call(root.querySelectorAll('.cmt__row'));

    var currentFilter = root.dataset.defaultFilter || 'all';

    /* ------------------------------------------------------ scroll sync */

    /* El lock se libera hasta el siguiente frame. Liberarlo de inmediato no
       sirve: asignar scrollLeft dispara el evento 'scroll' de forma asincrona,
       o sea despues de que el flag ya volvio a false — y los dos viewports se
       quedan rebotando el scroll uno contra otro. Eso era la lentitud. */
    var lock = null;

    function bindSync(from, to) {
      from.addEventListener('scroll', function () {
        if (lock === to) return;
        lock = from;
        to.scrollLeft = from.scrollLeft;
        updateThumb();
        requestAnimationFrame(function () {
          if (lock === from) lock = null;
        });
      }, { passive: true });
    }

    function updateThumb() {
      if (!thumb || !tableView) return;
      var max = tableView.scrollWidth - tableView.clientWidth;
      var track = thumb.parentElement;
      if (max <= 1) {
        track.style.visibility = 'hidden';
        navBtns.forEach(function (b) { b.disabled = true; });
        return;
      }
      track.style.visibility = 'visible';
      var ratio = tableView.clientWidth / tableView.scrollWidth;
      var width = Math.max(ratio * 100, 8);
      thumb.style.width = width + '%';
      var progress = tableView.scrollLeft / max;
      thumb.style.transform = 'translateX(' + (progress * (100 / width) * (100 - width)) + '%)';
      updateNav(max);
    }

    function updateNav(max) {
      navBtns.forEach(function (btn) {
        var dir = parseInt(btn.dataset.cmtNav, 10);
        if (dir < 0) btn.disabled = tableView.scrollLeft <= 1;
        else btn.disabled = tableView.scrollLeft >= max - 1;
      });
    }

    if (cardsView && tableView) {
      bindSync(cardsView, tableView);
      bindSync(tableView, cardsView);
    }

    /* Las flechas animan los DOS viewports a la vez con la misma curva, en vez
       de animar uno y que el otro lo persiga por el evento scroll. */
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function scrollByCols(dir) {
      var step = parseInt(getComputedStyle(root).getPropertyValue('--cmt-col-w'), 10) || 200;
      var max = tableView.scrollWidth - tableView.clientWidth;
      var from = tableView.scrollLeft;
      var to = Math.max(0, Math.min(max, from + dir * step * 3));
      if (to === from) return;

      if (reduceMotion) {
        lock = tableView;
        tableView.scrollLeft = to;
        cardsView.scrollLeft = to;
        updateThumb();
        lock = null;
        return;
      }

      var start = null;
      var dist = to - from;
      var dur = 320;

      function frame(now) {
        if (start === null) start = now;
        var t = Math.min((now - start) / dur, 1);
        var eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        var pos = from + dist * eased;
        lock = tableView;
        tableView.scrollLeft = pos;
        cardsView.scrollLeft = pos;
        updateThumb();
        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          lock = null;
        }
      }
      requestAnimationFrame(frame);
    }

    navBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        scrollByCols(parseInt(btn.dataset.cmtNav, 10));
      });
    });

    /* ------------------------------------------------- drag to scroll */

    function enableDrag(view) {
      if (!view) return;
      var down = false, moved = false, startX = 0, startScroll = 0, pid = null;

      function refresh() {
        var can = view.scrollWidth > view.clientWidth + 2;
        view.classList.toggle('cmt__viewport--draggable', can);
        if (!can) view.classList.remove('cmt__viewport--grabbing');
        return can;
      }
      refresh();
      window.addEventListener('resize', refresh, { passive: true });
      view.addEventListener('dragstart', function (e) { e.preventDefault(); });

      view.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        if (e.button !== 0) return;
        if (!refresh()) return;
        if (e.target.closest('a, button, input, label, select, textarea')) return;
        down = true; moved = false; pid = e.pointerId;
        startX = e.clientX;
        startScroll = (view === cardsView || view === tableView) && tableView ? tableView.scrollLeft : view.scrollLeft;
      });

      view.addEventListener('pointermove', function (e) {
        if (!down || e.pointerId !== pid) return;
        var dx = e.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) < 4) return;
          moved = true;
          view.classList.add('cmt__viewport--grabbing');
          try { view.setPointerCapture(pid); } catch (err) {}
        }
        e.preventDefault();
        var pos = startScroll - dx;
        if ((view === cardsView || view === tableView) && tableView && cardsView) {
          lock = tableView;
          tableView.scrollLeft = pos;
          cardsView.scrollLeft = pos;
          updateThumb();
        } else {
          view.scrollLeft = pos;
        }
      });

      function end(e) {
        if (!down || (e && e.pointerId !== pid)) return;
        down = false;
        view.classList.remove('cmt__viewport--grabbing');
        try { view.releasePointerCapture(pid); } catch (err) {}
        lock = null;
        if (moved) {
          view.addEventListener('click', function swallow(ev) {
            ev.preventDefault(); ev.stopPropagation();
          }, { capture: true, once: true });
        }
        pid = null;
      }
      view.addEventListener('pointerup', end);
      view.addEventListener('pointercancel', end);
      /* Red de seguridad: si el pointerup se pierde (se suelta fuera de la
         ventana, alt-tab), `down` y sobre todo `lock` se quedarian pegados y
         la sincronizacion de scroll dejaria de funcionar para siempre. */
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      window.addEventListener('blur', function () { end(); });
    }

    enableDrag(cardsView);
    enableDrag(tableView);
    enableDrag(root.querySelector('.cmt__picker-track'));


    if (jumpBtn && anchor) {
      jumpBtn.addEventListener('click', function () {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    /* ---------------------------------------------------------- filters */

    /* --------------------------------------------- modo movil (2 modelos)

       En movil no caben 9 columnas, asi que solo se comparan 2 a la vez.
       `picked` guarda los data-col elegidos, en orden de antiguedad: al elegir
       un tercero se reemplaza el mas viejo. El breakpoint debe coincidir con
       el de compare-machines.css. */

    var MOBILE_MQ = window.matchMedia('(max-width: 749px)');
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-cmt-chip]'));
    var picked = [];

    /* El hint "Swipe →" tambien avanza el strip al tocarlo: en un telefono no
       es obvio que el track se arrastra. Al llegar al final vuelve al inicio. */
    var pickerTrack = root.querySelector('.cmt__picker-track');
    var pickerHint  = root.querySelector('[data-cmt-picker-scroll]');

    if (pickerTrack && pickerHint) {
      pickerHint.addEventListener('click', function () {
        var max = pickerTrack.scrollWidth - pickerTrack.clientWidth;
        if (max <= 1) return;
        var next = pickerTrack.scrollLeft + pickerTrack.clientWidth * 0.8;
        if (pickerTrack.scrollLeft >= max - 2) next = 0;   // rebobina
        pickerTrack.scrollTo({
          left: Math.min(next, max),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      });
    }

    function isMobile() { return MOBILE_MQ.matches; }

    function categoryCols() {
      return cards
        .filter(function (c) {
          return currentFilter === 'all' || c.dataset.category === currentFilter;
        })
        .map(function (c) { return c.dataset.col; });
    }

    /* Deja `picked` con exactamente 2 columnas validas para el filtro actual. */
    function reconcilePicked() {
      var allowed = categoryCols();
      picked = picked.filter(function (col) { return allowed.indexOf(col) !== -1; });
      for (var i = 0; i < allowed.length && picked.length < 2; i++) {
        if (picked.indexOf(allowed[i]) === -1) picked.push(allowed[i]);
      }
    }

    function pick(col) {
      if (picked.indexOf(col) !== -1) return;   // ya esta comparada
      picked.push(col);
      while (picked.length > 2) picked.shift(); // fuera el mas viejo
      applyFilter();
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        pick(chip.dataset.col);
      });
    });

    /* --------------------------------------------------------- filtros */

    function activeColumns() {
      var allowed = categoryCols();
      if (!isMobile()) return allowed;
      reconcilePicked();
      // se respeta el orden original de columnas, no el de seleccion
      return allowed.filter(function (col) { return picked.indexOf(col) !== -1; });
    }

    function applyFilter() {
      var active = activeColumns();
      var allowed = categoryCols();

      cards.forEach(function (card) {
        card.classList.toggle('is-hidden', active.indexOf(card.dataset.col) === -1);
      });

      // los chips se filtran por categoria, no por seleccion
      chips.forEach(function (chip) {
        var inCat = allowed.indexOf(chip.dataset.col) !== -1;
        var isPicked = active.indexOf(chip.dataset.col) !== -1;
        chip.classList.toggle('is-hidden', !inCat);
        chip.classList.toggle('is-selected', isPicked);
        chip.setAttribute('aria-pressed', isPicked ? 'true' : 'false');
      });

      // toda celda con data-col dentro de la tabla (incluye el thead)
      Array.prototype.forEach.call(root.querySelectorAll('.cmt__table [data-col]'), function (cell) {
        cell.classList.toggle('is-hidden', active.indexOf(cell.dataset.col) === -1);
      });

      // el colspan del encabezado de grupo debe seguir al numero de columnas
      var span = active.length + 1;
      Array.prototype.forEach.call(root.querySelectorAll('.cmt__group-cell'), function (cell) {
        cell.setAttribute('colspan', span);
      });

      root.classList.toggle('cmt--duel', isMobile());

      applyDiff();
      requestAnimationFrame(updateThumb);
    }

    filterBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        currentFilter = btn.dataset.cmtFilter;
        filterBtns.forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        picked = [];          // al cambiar de filtro, vuelve a los 2 primeros
        applyFilter();
      });
    });

    if (MOBILE_MQ.addEventListener) {
      MOBILE_MQ.addEventListener('change', applyFilter);
    } else if (MOBILE_MQ.addListener) {
      MOBILE_MQ.addListener(applyFilter);   // Safari viejo
    }

    /* ------------------------------------------- show only differences */

    function applyDiff() {
      var on = diffInput && diffInput.checked;
      var hiddenCount = 0;
      var total = 0;

      rows.forEach(function (row) {
        if (!on) {
          row.classList.remove('is-hidden');
          return;
        }
        total++;
        var values = [];
        Array.prototype.forEach.call(row.querySelectorAll('.cmt__cell'), function (cell) {
          if (!cell.classList.contains('is-hidden')) {
            values.push(cell.dataset.value);
          }
        });

        var allSame = values.length > 1 && values.every(function (v) {
          return v === values[0];
        });

        row.classList.toggle('is-hidden', allSame);
        if (allSame) hiddenCount++;
      });

      if (noDiffMsg) {
        noDiffMsg.hidden = !(on && total > 0 && hiddenCount === total);
      }
    }

    if (diffInput) {
      diffInput.addEventListener('change', applyDiff);
    }

    /* ------------------------------------------------------- add to cart */

    /* El tema (Warehouse) hace el add to cart por AJAX y luego dispara un
       'product:added' que burbujea; el header lo escucha, re-renderiza el
       mini-cart y lo abre (solo si settings.cart_type === 'drawer').
       Replicamos exactamente ese contrato para que las cards de la tabla se
       comporten como la PDP en vez de mandar a /cart.

       Si el tema esta configurado en cart_type 'page', dejamos pasar el submit
       nativo: ahi la PDP tampoco abre popup. */

    function cartRoot() {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
      return '/';
    }

    function onAddToCart(event) {
      var form = event.target.closest('[data-cmt-atc-form]');
      if (!form || !root.contains(form)) return;

      if (window.theme && window.theme.cartType === 'page') return;

      event.preventDefault();

      var button = form.querySelector('.cmt__atc');
      var idInput = form.querySelector('[name="id"]');
      var qtyInput = form.querySelector('[name="quantity"]');
      if (!idInput) return;

      var variantId = Number(idInput.value);
      var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      // Only the plan the theme set for this variant. STOQ injects the page product's
      // selling_plan into every form, which would attach it to the wrong machine.
      var planInput = form.querySelector('[data-preorder-plan]');
      var item = { id: variantId, quantity: quantity };
      if (planInput && planInput.value) item.selling_plan = Number(planInput.value);
      var original = button ? button.innerHTML : '';

      if (button) {
        button.disabled = true;
        button.innerHTML = 'Adding…';
      }
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [item] })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function () {
          if (button) {
            button.disabled = false;
            button.innerHTML = original;
          }

          /* Se dispara desde el form (no desde document) para que burbujee
             hasta documentElement, que es donde el header tiene el delegate. */
          form.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              variant: { id: variantId },
              button: button,
              quantity: quantity
            }
          }));
        })
        .catch(function () {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          if (button) {
            button.disabled = false;
            button.innerHTML = 'Try again';
            window.setTimeout(function () {
              button.innerHTML = original;
            }, 2000);
          }
        });
    }

    root.addEventListener('submit', onAddToCart);

    /* ------------------------------------------------------------ init */

    if (currentFilter !== 'all') {
      filterBtns.forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.cmtFilter === currentFilter);
      });
    }

    applyFilter();
    /* ------------------------------------- columnas a lo ancho del hueco
       Tanto el grid de cards como la tabla leen --cmt-col-w, asi que
       recalcularla aqui mantiene las dos rejillas alineadas al pixel. */
    function fitColumns() {
      if (!root || !tableView) return;
      root.style.removeProperty('--cmt-col-w');
      root.style.removeProperty('--cmt-label-w');
      if (MOBILE_MQ.matches) return;
      var cs = getComputedStyle(root);
      var natural = parseFloat(cs.getPropertyValue('--cmt-col-w')) || 200;
      var cols = parseInt(cs.getPropertyValue('--cmt-cols'), 10) || 0;
      if (!cols) return;
      var corner = root.querySelector('.cmt__corner');
      var label = corner ? corner.getBoundingClientRect().width : 0;
      var avail = tableView.clientWidth - label;
      if (avail <= 0) return;
      var w = Math.floor(avail / cols);
      if (w > natural) {
        /* El sobrante por redondeo se le da a la columna de labels para que
           el total cuadre exacto con el ancho disponible. */
        root.style.setProperty('--cmt-col-w', w + 'px');
        root.style.setProperty('--cmt-label-w', (avail + label - w * cols) + 'px');
      }
    }

    fitColumns();
    window.addEventListener('resize', function () { fitColumns(); updateThumb(); }, { passive: true });
    window.addEventListener('resize', updateThumb, { passive: true });
    requestAnimationFrame(updateThumb);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cmt]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', boot);
})();
